// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { Share, type ShareOptions, type ShareResult } from "@capacitor/share";
import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_APP_EVENTS, isCapacitorPlatform } from "@src/appV2/lib";
import { useMutation } from "@tanstack/react-query";

import { type InviteMessage } from "../types";

interface ShareProfessionalReferenceParams {
  inviteMessage: InviteMessage;
}

export function useShareProfessionalReference() {
  return useMutation<ShareResult | undefined, unknown, ShareProfessionalReferenceParams>({
    mutationFn: async ({ inviteMessage }) => {
      const data: Pick<ShareOptions | ShareData, "title" | "text"> = {
        title: "Professional Reference Request",
        text: `${inviteMessage.messageText} ${inviteMessage.webFormLink}`,
      };

      // Use native share API if available
      if (isCapacitorPlatform()) {
        return await Share.share(data);
      }

      // Fallback to web share API
      if (!isDefined(navigator?.share)) {
        throw new Error("Share API is not supported on this browser");
      }

      await navigator.share(data);

      return undefined;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.SHARE_PROFESSIONAL_REFERENCE_FAILURE,
    },
  });
}
